import React, { useState, useMemo, useCallback, useEffect } from 'react'
import '../../utils/constants/CSS/eventForms.css'
import { useTranslate } from 'react-admin'
import { useFormikContext } from 'formik'
import { makeStyles, Switch, Typography, Tooltip } from '@material-ui/core'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { isFormFieldDisabled, isBroadcastEnded } from '../../utils/helpers/isFieldDisabled'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../utils/constants/gameEventFormBroadcastFields'
import getScoreboardSources from '../../Apis/scoreboard/getScoreboardSources'
import useQueryParams from '../../hooks/useQueryParams'
import eventFormLivestreamFieldsStyles from './eventFormLivestreamFields.styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SelectRecentLivestreamDialog from './SelectRecentLivestreamDialog/SelectRecentLivestreamDialog'
import MySelectField from '../form/fields/MySelectField/MySelectField'
import QueryKeys from '../../utils/constants/queryKeys'
import getLivestreamTypes from '../../Apis/event/getLivestreamTypes'
import setScoreboardSource from '../../Apis/scoreboard/setScoreboardSource'
import BROADCAST_STATES from '../../utils/constants/broadcastStates'
import COLORS from '../../utils/constants/colors'
import YoutubeStreamFields from '../YoutubeStreamFields/YoutubeStreamFields'
import LIVESTREAM_TYPE_OPTIONS from '../../utils/constants/livestreamTypeOptions'
import { STREAM_FEATURES, supports } from '../../utils/constants/streamTypeCapabilities'
import ExtraStreamField from '../YoutubeStreamFields/StreamFields/ExtraStreamField'
import ConditionalRender from '../ConditionalRender/ConditionalRender'
import InfoIcon from '@material-ui/icons/Info'
import BlockIcon from '@material-ui/icons/Block'
import VideocamIcon from '@material-ui/icons/Videocam'
import LiveTvIcon from '@material-ui/icons/LiveTv'
import VideocamOffIcon from '@material-ui/icons/VideocamOff'
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline'
import StopIcon from '@material-ui/icons/StopRounded'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import SectionFieldset from '../GameFormLayout/SectionFieldset'
import scoreboardSourceOptions, {
  canSwitchSourceLive as canSwitchSourceLiveRule,
  defaultScoreboardSource,
} from '../../utils/helpers/eventHelpers/scoreboardSourceOptions'
import USER_PERMISSIONS from '../../utils/constants/userPermissions'
import hasPermission from '../../utils/helpers/hasPermission'

const EventFormLivestreamFields = ({
  state,
  gameStateTrackingAutoEnabled,
  allowOrgYoutube,
  broadcast,
  broadcastId,
  selectedCourt,
  selectedEventType,
  showGameStateTracking,
  studioOperatorField,
  gameId,
}) => {
  const [showRecentSetups, setShowRecentSetups] = useState(false)
  const translate = useTranslate()
  const { values, setFieldValue } = useFormikContext()
  const useStyles = makeStyles(eventFormLivestreamFieldsStyles)
  const classes = useStyles()

  const isFormDisabled = useMemo(() => isFormFieldDisabled(state), [state])

  // The drawer is the event-editing surface, so switching a live stream here is for owners and
  // admins; an assigned operator does it from the banner or the Studio page instead.
  const canSwitchLive = hasPermission(USER_PERMISSIONS.UPDATE_EVENT)
  const isLiveBroadcast = state === BROADCAST_STATES.running || state === BROADCAST_STATES.paused

  const queryClient = useQueryClient()
  const liveScoreboardSource = useMutation(
    ({ source }) => setScoreboardSource(orgId, gameId, source),
    { onSuccess: () => queryClient.invalidateQueries(QueryKeys.GET_RUNNING_BROADCASTS_EVENTS) },
  )
  const isBroadcastEndedMemo = useMemo(() => (state ? isBroadcastEnded(state) : false), [state])

  // Coverage selector — three cards replacing the legacy 2-option toggle. Preserves the exact
  // isBroadcast/isRecord semantics of SwitchTripleOption:
  //   Off        -> isBroadcast=false, isRecord=false
  //   Record only-> isBroadcast=false, isRecord=true
  //   Livestream -> isBroadcast=true,  isRecord=false
  const activeCoverage = useMemo(() => {
    if (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]) return 'livestream'
    if (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) return 'record'
    return 'off'
  }, [
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST],
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
  ])

  const handleCoverageChange = useCallback(
    (mode) => {
      if (isFormDisabled) return
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST, mode === 'livestream')
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD, mode === 'record')
    },
    [isFormDisabled, setFieldValue],
  )

  const coverageOptions = [
    { key: 'livestream', label: translate('ra.gameDrawer.coverage.livestream'), Icon: LiveTvIcon },
    { key: 'off', label: translate('ra.gameDrawer.coverage.off'), Icon: BlockIcon },
    { key: 'record', label: translate('ra.gameDrawer.coverage.recordOnly'), Icon: VideocamIcon },
  ]

  const { data } = useQuery([QueryKeys.GET_LIVESTREAM_TYPES, selectedEventType?.id], () =>
    getLivestreamTypes({ eventTypeCode: selectedEventType?.id }),
  )
  const parsedLivestreamTypes = useMemo(
    () =>
      data
        ?.filter((datum) => datum.name !== LIVESTREAM_TYPE_OPTIONS.LIVESTREAM_STATIC)
        .map((datum) => ({
          id: datum.name,
          name: datum.displayName,
        })),
    [data],
  )

  const isLivestreamTypeFull = useMemo(
    () =>
      (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
        values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) &&
      supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.COURT_LOGOS),
    [
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
    ],
  )

  // The commentary picker (off-site / on-site). Livestream-only like Studio.
  const supportsOnSiteCommentary = useMemo(
    () =>
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] &&
      supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.STUDIO),
    [
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
    ],
  )

  // Livestream-only, for parity with remote commentary (Studio): a record-only game has nobody
  // to commentate to, so the switch is hidden rather than sent and ignored.
  const supportsLocalCommentary = useMemo(
    () =>
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] &&
      supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.LOCAL_COMMENTARY),
    [
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
    ],
  )

  const isLivestreamTypeLite = useMemo(
    () =>
      (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
        values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) &&
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE] === LIVESTREAM_TYPE_OPTIONS.LIVESTREAM_LITE,
    [
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
    ],
  )

  // Get current tracking state
  const getCurrentTrackingState = useCallback(() => {
    if (values[GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO]) {
      return 'auto'
    }
    if (values[GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL]) {
      return 'manual'
    }
    return 'disabled'
  }, [
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO],
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL],
  ])

  // Handle tracking state change
  const handleTrackingStateChange = useCallback(
    (event, newValue) => {
      if (!newValue) return // Prevent deselection

      // Reset both fields first
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO, false)
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL, false)

      // Set the appropriate field based on selection
      switch (newValue) {
        case 'auto':
          setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO, true)
          break
        case 'manual':
          setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL, true)
          break
        case 'disabled':
          // Both fields already set to false above
          break
        default:
          break
      }
    },
    [setFieldValue],
  )

  // Automation toggles rendered as two selectable cards (design: icon + title + check
  // indicator), replacing the legacy label/Switch rows. Same field + toggle semantics. The
  // description shows as a tooltip on hover instead of inline subtext.
  const renderAutomationCard = ({ field, Icon, title, tooltip }) => {
    const active = Boolean(values[field])
    const accent = active ? '#E06E5C' : '#8A8A8A'
    const disabled = isFormFieldDisabled(state, field)
    return (
      <Tooltip title={<h3 style={{ fontStyle: 'oblique' }}>{tooltip}</h3>} arrow>
        <div
          role='button'
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && setFieldValue(field, !values[field])}
          onKeyDown={(event) => {
            if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault()
              setFieldValue(field, !values[field])
            }
          }}
          style={{
            // flex-basis 0 + minWidth 0 so both cards get exactly half the row regardless of
            // their content width (otherwise the default min-width:auto can leave them uneven).
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '12px 13px',
            borderRadius: 8,
            border: `1.5px solid ${active ? '#E06E5C' : '#E2E2E2'}`,
            backgroundColor: active ? '#FCEFEC' : '#FFFFFF',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            outline: 'none',
          }}
        >
          <Icon style={{ fontSize: 20, color: accent }} />
          <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: accent }}>{title}</div>
          {active ? (
            <CheckCircleIcon style={{ fontSize: 20, color: accent }} />
          ) : (
            <RadioButtonUncheckedIcon style={{ fontSize: 20, color: accent }} />
          )}
        </div>
      </Tooltip>
    )
  }

  // Which sources this event may feed its scoreboard from is the server's answer, not ours. A saved
  // event carries it on the broadcast; a new one has to ask, because the court's own list cannot
  // include Studio — that depends on the tier and on the org's rollout, neither of which a court
  // knows. Only asked while the event does not exist yet and both inputs are known.
  const { org: orgId } = useQueryParams()
  const livestreamType = values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE]
  const askServer = !broadcast && !!orgId && !!selectedCourt?.id && !!livestreamType
  const { data: prospectiveSources } = useQuery(
    [QueryKeys.GET_SCOREBOARD_SOURCES, orgId, selectedCourt?.id, livestreamType],
    () =>
      getScoreboardSources(orgId, { courtId: selectedCourt.id, streamType: livestreamType }),
    { enabled: askServer },
  )

  const scoreboardSources = useMemo(
    () =>
      scoreboardSourceOptions(
        // The answer for a game that does not exist yet stands in for the saved broadcast's, so the
        // picker has one shape to read and the create and edit forms agree.
        broadcast || (prospectiveSources ? { supportedScoreboardSources: prospectiveSources } : null),
        selectedCourt,
        livestreamType,
      ),
    [broadcast, prospectiveSources, selectedCourt, livestreamType],
  )
  const scoreboardSource = values[GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE]

  // A source can stop being on offer while the form is open: the tier drops to Plus, the court
  // changes. The backend rejects a source its rules disallow rather than downgrading it, so the
  // field is put back to the default instead of being left holding a value that fails the save.
  // This also seeds a new event, whose source starts unset.
  useEffect(() => {
    if (scoreboardSources.some((option) => option.value === scoreboardSource)) return
    setFieldValue(
      GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE,
      defaultScoreboardSource(scoreboardSources),
    )
  }, [scoreboardSources, scoreboardSource, setFieldValue])

  const canSwitchSourceLive = canSwitchSourceLiveRule({
    isLive: isLiveBroadcast,
    orgId,
    gameId,
    canControl: canSwitchLive,
  })

  const handleScoreboardSourceChange = useCallback(
    (_event, nextSource) => {
      // An exclusive group emits null when the selected button is clicked again; a scoreboard
      // always has a source, so the current one is kept rather than cleared.
      if (!nextSource) return
      const previous = scoreboardSource
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE, nextSource)
      if (!canSwitchSourceLive) return
      liveScoreboardSource.mutate(
        { source: nextSource },
        {
          onError: () =>
            setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.SCOREBOARD_SOURCE, previous),
        },
      )
    },
    [canSwitchSourceLive, liveScoreboardSource, scoreboardSource, setFieldValue],
  )

  const renderScoreboardSourceSection = () => {
    const currentSource = scoreboardSource ?? defaultScoreboardSource(scoreboardSources)

    return (
      <div className='div-flex-space-between'>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography style={{ fontSize: 14, fontWeight: 500, color: '#3C3C3C' }}>
            {translate('ra.gameDrawer.scoreboardSource')}
          </Typography>
          <Tooltip
            title={
              <h3 style={{ fontStyle: 'oblique' }}>{translate('ra.text.scoreboardSourceInfo')}</h3>
            }
            arrow
          >
            <InfoIcon style={{ color: COLORS.grey, fontSize: 16 }} />
          </Tooltip>
        </div>
        <ToggleButtonGroup
          value={currentSource}
          exclusive
          size='small'
          onChange={handleScoreboardSourceChange}
          aria-label='scoreboard source'
          className={classes.toggleGroup}
        >
          {scoreboardSources.map(({ value, label }) => (
            <ToggleButton
              key={value}
              value={value}
              disabled={canSwitchSourceLive ? liveScoreboardSource.isLoading : isFormDisabled}
              style={{
                backgroundColor: currentSource === value ? COLORS.orange : undefined,
                color: currentSource === value ? COLORS.white : undefined,
                fontSize: '12px',
                padding: '4px 10px',
              }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    )
  }

  // Off-site (OffSite, the web Studio) or on-site (OnSite), saved with the game like the other
  // fields; while live, the save switches the running stream (the commentator on air goes off air).
  // Off-site has no camera mic, so choosing it turns that off too.
  const commentaryMode = values[GAME_EVENT_FORM_BROADCAST_FIELDS.COMMENTARY_MODE]
  const isOnSiteCommentary = commentaryMode === 'OnSite'

  const handleCommentaryChange = useCallback(
    (_event, next) => {
      // An exclusive group emits null when the selected button is clicked again; keep it.
      if (!next || next === commentaryMode) return
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.COMMENTARY_MODE, next)
      if (next !== 'OnSite') setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON, false)
    },
    [setFieldValue, commentaryMode],
  )

  const renderCommentarySection = () => {
    const options = [
      { value: 'OffSite', label: translate('ra.gameDrawer.commentary.offSite') },
      { value: 'OnSite', label: translate('ra.gameDrawer.commentary.onSite') },
    ]

    return (
      <div className='div-flex-space-between'>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography style={{ fontSize: 14, fontWeight: 500, color: '#3C3C3C' }}>
            {translate('ra.gameDrawer.commentary.label')}
          </Typography>
          <Tooltip
            title={
              <h3 style={{ fontStyle: 'oblique' }}>
                {translate('ra.gameDrawer.commentary.info')}
              </h3>
            }
            arrow
          >
            <InfoIcon style={{ color: COLORS.grey, fontSize: 16 }} />
          </Tooltip>
        </div>
        <ToggleButtonGroup
          value={commentaryMode}
          exclusive
          size='small'
          onChange={handleCommentaryChange}
          aria-label='commentary'
          className={classes.toggleGroup}
        >
          {options.map(({ value, label }) => (
            <ToggleButton
              key={value}
              value={value}
              disabled={isFormFieldDisabled(state, GAME_EVENT_FORM_BROADCAST_FIELDS.COMMENTARY_MODE)}
              style={{
                backgroundColor: commentaryMode === value ? COLORS.orange : undefined,
                color: commentaryMode === value ? COLORS.white : undefined,
                fontSize: '12px',
                padding: '4px 10px',
              }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    )
  }

  const renderGameStateTrackingSection = () => {
    const currentState = getCurrentTrackingState()
    // AUTO is only offered when the court supports auto tracking; otherwise DISABLED/MANUAL.
    const options = gameStateTrackingAutoEnabled
      ? [
          ['auto', 'Auto'],
          ['disabled', 'Disabled'],
          ['manual', 'Manual'],
        ]
      : [
          ['disabled', 'Disabled'],
          ['manual', 'Manual'],
        ]
    const groupValue = gameStateTrackingAutoEnabled
      ? currentState
      : currentState === 'manual'
        ? 'manual'
        : 'disabled'

    return (
      <div className='div-flex-space-between'>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Typography style={{ fontSize: 14, fontWeight: 500, color: '#3C3C3C' }}>
              {translate('ra.gameDrawer.streamEventTracking')}
            </Typography>
            <Tooltip
              title={
                <h3 style={{ fontStyle: 'oblique' }}>{translate('ra.text.gameStateTrackingInfo')}</h3>
              }
              arrow
            >
              <InfoIcon style={{ color: COLORS.grey, fontSize: 16 }} />
            </Tooltip>
          </div>
        </div>
        <ToggleButtonGroup
          value={groupValue}
          exclusive
          size='small'
          onChange={handleTrackingStateChange}
          aria-label='tracking mode'
          className={classes.toggleGroup}
        >
          {options.map(([val, labelText]) => (
            <ToggleButton
              key={val}
              value={val}
              style={{
                backgroundColor: currentState === val ? COLORS.orange : undefined,
                color: currentState === val ? COLORS.white : undefined,
                fontSize: '12px',
                padding: '4px 10px',
              }}
            >
              {labelText}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    )
  }

  return (
    <div className='div-flex-column'>
      <div style={{ display: 'flex', gap: 8 }}>
        {coverageOptions.map(({ key, label, Icon }) => {
          const active = activeCoverage === key
          return (
            <div
              key={key}
              role='button'
              tabIndex={isFormDisabled ? -1 : 0}
              onClick={() => handleCoverageChange(key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleCoverageChange(key)
                }
              }}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 6px',
                borderRadius: 10,
                border: `1.5px solid ${active ? '#E06E5C' : '#E2E2E2'}`,
                backgroundColor: active ? '#FCEFEC' : '#FFFFFF',
                cursor: isFormDisabled ? 'not-allowed' : 'pointer',
                opacity: isFormDisabled ? 0.6 : 1,
                outline: 'none',
              }}
            >
              <Icon style={{ fontSize: 19, color: active ? '#E06E5C' : '#A0A0A0' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#3C3C3C' }}>{label}</span>
            </div>
          )
        })}
      </div>

      <ConditionalRender renderValue={activeCoverage === 'off'}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            backgroundColor: '#F7F6F4',
            borderRadius: 10,
            padding: '14px 16px',
          }}
        >
          <VideocamOffIcon style={{ color: '#A0A0A0' }} />
          <span style={{ fontSize: 13, color: '#6B6B6B' }}>
            {translate('ra.gameDrawer.offNote')}
          </span>
        </div>
      </ConditionalRender>

      <ConditionalRender renderValue={values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]}>
        <div
          role='button'
          tabIndex={isBroadcastEndedMemo ? -1 : 0}
          onClick={() => !isBroadcastEndedMemo && setShowRecentSetups((v) => !v)}
          onKeyDown={(event) => {
            if (!isBroadcastEndedMemo && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault()
              setShowRecentSetups((v) => !v)
            }
          }}
          style={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: '#E06E5C',
            fontSize: 13,
            fontWeight: 600,
            cursor: isBroadcastEndedMemo ? 'default' : 'pointer',
            opacity: isBroadcastEndedMemo ? 0.5 : 1,
            outline: 'none',
          }}
        >
          {translate('ra.buttons.loadRecentSetups')}
          <ExpandMoreIcon
            style={{
              fontSize: 18,
              transition: 'transform .2s',
              transform: showRecentSetups ? 'rotate(180deg)' : 'none',
            }}
          />
        </div>
        <SelectRecentLivestreamDialog
          expanded={showRecentSetups}
          onCollapse={() => setShowRecentSetups(false)}
        />
      </ConditionalRender>

      <ConditionalRender renderValue={values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]}>
        <SectionFieldset label={translate('ra.gameDrawer.sections.destination')}>
          <YoutubeStreamFields
            state={state}
            allowOrgYoutube={allowOrgYoutube}
            broadcastId={broadcastId}
          />

          <ExtraStreamField state={state} variant='field' />

          <ExtraStreamField state={state} variant='addLink' />
        </SectionFieldset>
      </ConditionalRender>

      <ConditionalRender
        renderValue={
          values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
          values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]
        }
      >
        <SectionFieldset
          label={translate('ra.gameDrawer.sections.production')}
          // Match Destination's field rhythm (YoutubeStreamFields spaces its fields at 11px).
          contentGap={11}
          // The last row's MUI Switch has ~12px of built-in padding, so its visible toggle sits
          // that much inside its box — making the bottom gap read larger than the padding. Shrink
          // paddingBottom by ~12 to cancel that inset and match the top gap.
          style={{ paddingTop: 26, paddingBottom: 12 }}
        >
          {(values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
            values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) && (
            <MySelectField
              name={GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE}
              label={translate('ra.formFields.livestreamType')}
              options={parsedLivestreamTypes}
              disabled={isFormDisabled}
              fullWidth
            />
          )}

          {studioOperatorField}

          {isLivestreamTypeFull && showGameStateTracking && renderGameStateTrackingSection()}

          {/* Worth asking only when something other than Off can actually feed the board. */}
          {isLivestreamTypeFull && scoreboardSources.length > 1 && renderScoreboardSourceSection()}

          {(isLivestreamTypeFull || isLivestreamTypeLite) && (
            <div className='div-flex-space-between'>
              <Typography style={{ fontSize: 14, fontWeight: 500, color: '#3C3C3C' }}>
                {translate('ra.text.autofollowEnabled')}
              </Typography>
              <Switch
                name='autofollowEnabled'
                color='primary'
                checked={values.autofollowEnabled}
                onChange={() => setFieldValue('autofollowEnabled', !values.autofollowEnabled)}
              />
            </div>
          )}

          {supportsOnSiteCommentary && renderCommentarySection()}

          {/* Via camera is a sub-option of on-site commentary (indented on a guide line); a game
              saved with it on before the picker existed still shows it, so it can be turned off. */}
          {supportsLocalCommentary &&
            (isOnSiteCommentary || values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON]) && (
            <div
              className='div-flex-space-between'
              style={{
                marginTop: -4,
                marginLeft: 6,
                paddingLeft: 14,
                borderLeft: '2px solid #3C3C3C',
              }}
            >
              <Typography style={{ fontSize: 13, fontWeight: 500, color: '#3C3C3C' }}>
                {translate('ra.gameDrawer.commentary.viaCamera')}
              </Typography>
              <Switch
                name={GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON}
                color='primary'
                checked={Boolean(values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON])}
                // Saved with the game; while live, the save moves the camera's audio source.
                disabled={isFormFieldDisabled(state, GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON)}
                onChange={() =>
                  setFieldValue(
                    GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON,
                    !values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_COMMENTARY_ON],
                  )
                }
              />
            </div>
          )}
        </SectionFieldset>
      </ConditionalRender>

      <ConditionalRender
        renderValue={
          (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
            values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) &&
          selectedEventType.name === 'Game'
        }
      >
        <SectionFieldset label={translate('ra.gameDrawer.sections.automation')}>
          <div style={{ display: 'flex', gap: 10 }}>
            {renderAutomationCard({
              field: GAME_EVENT_FORM_BROADCAST_FIELDS.AUTO_START,
              Icon: PlayCircleOutlineIcon,
              title: translate('ra.gameDrawer.automation.autoStart'),
              tooltip: translate('ra.gameDrawer.automation.autoStartDesc'),
            })}
            {renderAutomationCard({
              field: GAME_EVENT_FORM_BROADCAST_FIELDS.AUTO_STOP,
              Icon: StopIcon,
              title: translate('ra.gameDrawer.automation.autoStop'),
              tooltip: translate('ra.gameDrawer.automation.autoStopDesc'),
            })}
          </div>
        </SectionFieldset>
      </ConditionalRender>
    </div>
  )
}

EventFormLivestreamFields.defaultProps = {
  showGameStateTracking: false,
  studioOperatorField: null,
  gameId: null,
}

EventFormLivestreamFields.propTypes = {
  state: PropTypes.string,
  gameStateTrackingAutoEnabled: PropTypes.bool,
  allowOrgYoutube: PropTypes.bool,
  broadcast: PropTypes.shape({
    id: PropTypes.string,
    supportedScoreboardSources: PropTypes.arrayOf(PropTypes.string),
  }),
  broadcastId: PropTypes.string,
  selectedCourt: PropTypes.shape({
    id: PropTypes.string,
    scoreboardSources: PropTypes.arrayOf(PropTypes.string),
  }),
  selectedEventType: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  showGameStateTracking: PropTypes.bool,
  studioOperatorField: PropTypes.node,
  // Only a saved game can be switched live; absent on create, and on practices, which the
  // games-scoped endpoint does not cover.
  gameId: PropTypes.string,
}

export default EventFormLivestreamFields
